'use client';

import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Icon } from '@/components/ui/icon';
import { Alert01IconSolid, Cancel01Icon, Upload01Icon } from '@/components/ui/icon/glyphs';
import { cn } from '@/lib/ui/utils';

interface OutcomeImageFieldProps {
  index: number;
  displaySrc: string | null;
  isNsfw: boolean;
  imageRequired: boolean;
  imageError: string | null | undefined;
  imageLoading: boolean;
  eagerImage: boolean;
  pinFailed: boolean;
  onOpen: () => void;
  onRemove: () => void;
  children: ReactNode;
}

export function OutcomeImageField({
  index,
  displaySrc,
  isNsfw,
  imageRequired,
  imageError,
  imageLoading,
  eagerImage,
  pinFailed,
  onOpen,
  onRemove,
  children,
}: OutcomeImageFieldProps): ReactNode {
  const t = useTranslations('launchpad.form');
  const imageLabelId = `outcome-image-${String(index)}-label`;
  const imageStatusId = `outcome-image-${String(index)}-status`;
  const hasImageError =
    !imageLoading && imageError !== undefined && imageError !== null && imageError.length > 0;
  const imageStatusMessage = imageLoading
    ? t('outcomeImageUploading')
    : hasImageError
      ? imageError
      : null;

  return (
    <>
      {imageRequired ? (
        <div
          id={imageLabelId}
          className="flex items-center gap-1 text-caption font-medium text-foreground-secondary"
        >
          <span>{t('outcomeImageLabel')}</span>
          <span aria-hidden="true">·</span>
          <span>{t('outcomeImageRequiredLabel')}</span>
        </div>
      ) : null}

      <div className="flex items-start gap-3">
        <div className="size-16 shrink-0" data-testid="outcome-image-control">
          {displaySrc !== null ? (
            <div className="relative size-full">
              <OptimizedImage
                src={displaySrc}
                alt={t('outcomeImageAlt', { number: index + 1 })}
                variant="thumbnail"
                size="size-full"
                sizes="64px"
                rounded="rounded-card"
                className="block size-full"
                nsfw={isNsfw}
                priority={eagerImage}
              />
              {pinFailed ? (
                <div
                  className="absolute top-0.5 left-0.5 z-[5] flex size-4 items-center justify-center rounded-full bg-background-secondary/90 text-feedback-warning-content shadow-elevation-card"
                  title={t('outcomeImageFailedTitle', { number: index + 1 })}
                >
                  <Icon icon={Alert01IconSolid} className="size-2.5" />
                </div>
              ) : null}
              <button
                type="button"
                aria-label={t('outcomeImageRemove')}
                aria-describedby={imageLoading ? imageStatusId : undefined}
                aria-busy={imageLoading || undefined}
                data-testid="outcome-image-remove"
                onClick={onRemove}
                className="absolute top-0.5 right-0.5 z-10 flex size-4 items-center justify-center rounded-full bg-background-secondary/90 text-foreground-primary shadow-elevation-card transition-colors hover:bg-background-secondary"
              >
                <Icon icon={Cancel01Icon} className="size-2.5" />
              </button>
            </div>
          ) : imageLoading ? (
            <Skeleton className="size-full rounded-card" />
          ) : (
            <button
              type="button"
              onClick={onOpen}
              data-testid="outcome-image-trigger"
              aria-label={t('outcomeImageAddAriaLabel')}
              aria-describedby={imageStatusMessage === null ? undefined : imageStatusId}
              aria-invalid={hasImageError || undefined}
              className={cn(
                'group relative flex size-full cursor-pointer items-center justify-center overflow-hidden rounded-card border-2 border-dashed border-border-primary bg-background-tertiary transition-colors hover:border-border-brand hover:bg-background-tertiary/70',
                hasImageError && 'border-feedback-error-border hover:border-feedback-error-border',
              )}
            >
              <Icon
                icon={Upload01Icon}
                className="size-4 text-foreground-secondary transition-colors group-hover:text-foreground-brand"
              />
            </button>
          )}
        </div>

        {children}
      </div>

      {imageStatusMessage !== null ? (
        <p
          id={imageStatusId}
          role={hasImageError ? 'alert' : 'status'}
          className={cn(
            'text-caption',
            hasImageError ? 'text-feedback-error-content' : 'text-foreground-secondary',
          )}
        >
          {imageStatusMessage}
        </p>
      ) : null}
    </>
  );
}
